import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { api, ApiError, getAccessToken } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/app/$propertyId/equipment')({
  component: PropertyEquipment,
});

type Equipment = {
  id: string;
  name: string;
  type: string;
  modelNumber: string | null;
  usageSeasonStart: string;
  usageSeasonEnd: string;
  checkInsRequired: boolean;
  outOfCommissionAt: string | null;
  archivedAt: string | null;
  isInsideSeason: boolean;
  isCheckInOverdue: boolean;
  nextCheckInDueAt: string | null;
  notOkFlagCount: number;
  thumbnailUrl: string | null;
};

type CheckIn = {
  id: string;
  engineHours: number;
  notes: string | null;
  performedOn: string;
  flagValues: Array<{ id: string; flagKeySnapshot: string; flagLabelSnapshot: string; isOk: boolean }>;
};

type PropertyDetail = { role: 'owner' | 'member' | null };

type ConditionFlag = {
  id: string;
  equipmentId: string;
  key: string;
  label: string;
  source: string;
  displayOrder: number;
  archivedAt: string | null;
};

type MaintenanceSchedule = {
  id: string;
  equipmentId: string;
  name: string;
  triggerKind: 'time_based' | 'engine_hours' | 'calendar_date';
  triggerConfig: Record<string, unknown>;
  instructions: string | null;
  nextDueAt: string | null;
  archivedAt: string | null;
  createdAt: string;
};

const EQUIPMENT_TYPES = [
  { value: 'riding_mower', label: 'Riding mower' },
  { value: 'push_mower', label: 'Push mower' },
  { value: 'trimmer', label: 'Trimmer' },
  { value: 'tractor', label: 'Tractor' },
  { value: 'other', label: 'Other' },
] as const;

const FLAG_PRESETS = [
  { key: 'oil', label: 'Oil' },
  { key: 'tires', label: 'Tires' },
  { key: 'belt', label: 'Belt' },
  { key: 'axle', label: 'Axle' },
];

type EditState = {
  name: string;
  type: string;
  modelNumber: string;
  usageSeasonStart: string;
  usageSeasonEnd: string;
  checkInsRequired: boolean;
};

function FlagsPanel({ equipmentId, isOwner }: { equipmentId: string; isOwner: boolean }) {
  const qc = useQueryClient();
  const [addingFlag, setAddingFlag] = useState(false);
  const [flagPreset, setFlagPreset] = useState<string>('oil');
  const [customLabel, setCustomLabel] = useState('');

  const { data: flagsData, isPending } = useQuery({
    queryKey: ['flags', equipmentId],
    queryFn: () =>
      api
        .get<{ flags: ConditionFlag[] }>(`/v1/equipment/${equipmentId}/condition-flags`)
        .then((r) => r.flags),
  });

  const archiveFlagMutation = useMutation({
    mutationFn: (flagId: string) =>
      api.delete(`/v1/condition-flags/${flagId}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['flags', equipmentId] }),
  });

  const addFlagMutation = useMutation({
    mutationFn: (body: { key: string; label: string }) =>
      api.post(`/v1/equipment/${equipmentId}/condition-flags`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['flags', equipmentId] });
      setAddingFlag(false);
      setFlagPreset('oil');
      setCustomLabel('');
    },
  });

  const handleAddFlag = () => {
    if (flagPreset === 'custom') {
      const trimmed = customLabel.trim();
      if (!trimmed) return;
      addFlagMutation.mutate({ key: `custom_${Date.now()}`, label: trimmed });
    } else {
      const preset = FLAG_PRESETS.find((p) => p.key === flagPreset);
      if (!preset) return;
      addFlagMutation.mutate({ key: preset.key, label: preset.label });
    }
  };

  const activeFlags = flagsData?.filter((f) => f.archivedAt === null) ?? [];

  return (
    <div className="mt-2 pt-2 border-t border-border">
      {isPending ? (
        <p className="text-xs text-muted-foreground">Loading flags…</p>
      ) : (
        <>
          {activeFlags.length === 0 && (
            <p className="text-xs text-muted-foreground">No active flags.</p>
          )}
          <div className="flex flex-col gap-1">
            {activeFlags.map((flag) => (
              <div key={flag.id} className="flex items-center gap-2">
                <span className="text-xs">{flag.label}</span>
                {flag.source !== 'custom' && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                    preset
                  </span>
                )}
                {isOwner && (
                  <button
                    className="text-[10px] text-muted-foreground hover:text-destructive ml-auto disabled:opacity-50"
                    onClick={() => archiveFlagMutation.mutate(flag.id)}
                    disabled={archiveFlagMutation.isPending}
                  >
                    Remove
                  </button>
                )}
              </div>
            ))}
          </div>

          {isOwner && (
            <div className="mt-2">
              {!addingFlag ? (
                <button
                  className="text-xs text-muted-foreground hover:text-foreground"
                  onClick={() => setAddingFlag(true)}
                >
                  + Add flag
                </button>
              ) : (
                <div className="flex flex-col gap-1.5">
                  <div className="flex gap-2 items-end">
                    <div className="flex flex-col gap-0.5">
                      <label className="text-[10px] text-muted-foreground">Preset</label>
                      <select
                        value={flagPreset}
                        onChange={(e) => setFlagPreset(e.target.value)}
                        className="rounded border border-input bg-background px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-ring"
                      >
                        {FLAG_PRESETS.map((p) => (
                          <option key={p.key} value={p.key}>{p.label}</option>
                        ))}
                        <option value="custom">Custom…</option>
                      </select>
                    </div>
                    {flagPreset === 'custom' && (
                      <div className="flex flex-col gap-0.5">
                        <label className="text-[10px] text-muted-foreground">Label</label>
                        <input
                          type="text"
                          value={customLabel}
                          onChange={(e) => setCustomLabel(e.target.value)}
                          placeholder="Flag label"
                          className="rounded border border-input bg-background px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-ring w-32"
                        />
                      </div>
                    )}
                    <Button
                      size="sm"
                      className="h-7 text-xs px-2"
                      onClick={handleAddFlag}
                      disabled={
                        addFlagMutation.isPending ||
                        (flagPreset === 'custom' && !customLabel.trim())
                      }
                    >
                      {addFlagMutation.isPending ? 'Adding…' : 'Add'}
                    </Button>
                    <button
                      className="text-xs text-muted-foreground hover:text-foreground"
                      onClick={() => {
                        setAddingFlag(false);
                        setCustomLabel('');
                        setFlagPreset('oil');
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                  {addFlagMutation.isError && (
                    <p className="text-xs text-destructive">
                      {addFlagMutation.error instanceof ApiError
                        ? addFlagMutation.error.message
                        : 'Add failed.'}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function SchedulesPanel({ equipmentId, isOwner }: { equipmentId: string; isOwner: boolean }) {
  const qc = useQueryClient();
  const [addingSchedule, setAddingSchedule] = useState(false);
  const [scheduleForm, setScheduleForm] = useState({
    name: '',
    triggerKind: 'time_based' as 'time_based' | 'engine_hours' | 'calendar_date',
    everyNDays: 30,
    everyNHours: 25,
    anchor: 'season_start' as 'season_start' | 'season_end',
    instructions: '',
  });

  const { data: schedulesData, isPending } = useQuery({
    queryKey: ['schedules', equipmentId],
    queryFn: () =>
      api
        .get<{ schedules: MaintenanceSchedule[] }>(`/v1/equipment/${equipmentId}/schedules`)
        .then((r) => r.schedules),
  });

  const archiveScheduleMutation = useMutation({
    mutationFn: (scheduleId: string) =>
      api.post(`/v1/schedules/${scheduleId}/archive`, {}),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['schedules', equipmentId] }),
  });

  const addScheduleMutation = useMutation({
    mutationFn: (body: {
      name: string;
      triggerKind: string;
      triggerConfig: Record<string, unknown>;
      instructions?: string;
    }) => api.post(`/v1/equipment/${equipmentId}/schedules`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['schedules', equipmentId] });
      setAddingSchedule(false);
      setScheduleForm({
        name: '',
        triggerKind: 'time_based',
        everyNDays: 30,
        everyNHours: 25,
        anchor: 'season_start',
        instructions: '',
      });
    },
  });

  const handleAddSchedule = () => {
    const name = scheduleForm.name.trim();
    if (!name) return;
    let triggerConfig: Record<string, unknown>;
    if (scheduleForm.triggerKind === 'time_based') {
      triggerConfig = { everyNDays: scheduleForm.everyNDays };
    } else if (scheduleForm.triggerKind === 'engine_hours') {
      triggerConfig = { everyNHours: scheduleForm.everyNHours };
    } else {
      triggerConfig = { anchor: scheduleForm.anchor };
    }
    const trimmedInstructions = scheduleForm.instructions.trim();
    addScheduleMutation.mutate({
      name,
      triggerKind: scheduleForm.triggerKind,
      triggerConfig,
      ...(trimmedInstructions ? { instructions: trimmedInstructions } : {}),
    });
  };

  const activeSchedules = schedulesData?.filter((s) => s.archivedAt === null) ?? [];

  const triggerKindLabel = (kind: string) => {
    if (kind === 'time_based') return 'Time';
    if (kind === 'engine_hours') return 'Hours';
    if (kind === 'calendar_date') return 'Seasonal';
    return kind;
  };

  const formatDate = (iso: string | null) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString();
  };

  return (
    <div className="mt-2 pt-2 border-t border-border">
      {isPending ? (
        <p className="text-xs text-muted-foreground">Loading schedules…</p>
      ) : (
        <>
          {activeSchedules.length === 0 && (
            <p className="text-xs text-muted-foreground">No active schedules.</p>
          )}
          <div className="flex flex-col gap-2">
            {activeSchedules.map((s) => (
              <div key={s.id} className="rounded border border-border px-3 py-2">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium">{s.name}</span>
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                    {triggerKindLabel(s.triggerKind)}
                  </span>
                  <span className="text-xs text-muted-foreground ml-auto shrink-0">
                    Due {formatDate(s.nextDueAt)}
                  </span>
                  {isOwner && (
                    <button
                      className="text-[10px] text-muted-foreground hover:text-destructive disabled:opacity-50"
                      onClick={() => archiveScheduleMutation.mutate(s.id)}
                      disabled={archiveScheduleMutation.isPending}
                    >
                      Archive
                    </button>
                  )}
                </div>
                {s.instructions && (
                  <p className="text-xs text-muted-foreground mt-1">{s.instructions}</p>
                )}
              </div>
            ))}
          </div>

          {isOwner && (
            <div className="mt-2">
              {!addingSchedule ? (
                <button
                  className="text-xs text-muted-foreground hover:text-foreground"
                  onClick={() => setAddingSchedule(true)}
                >
                  + Add schedule
                </button>
              ) : (
                <div className="flex flex-col gap-2 rounded border border-border px-3 py-2">
                  <div className="grid grid-cols-2 gap-2">
                    <div className="flex flex-col gap-0.5">
                      <label className="text-[10px] text-muted-foreground">Name</label>
                      <input
                        type="text"
                        value={scheduleForm.name}
                        onChange={(e) =>
                          setScheduleForm((f) => ({ ...f, name: e.target.value }))
                        }
                        placeholder="e.g. Oil change"
                        className="rounded border border-input bg-background px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-ring"
                      />
                    </div>
                    <div className="flex flex-col gap-0.5">
                      <label className="text-[10px] text-muted-foreground">Trigger</label>
                      <select
                        value={scheduleForm.triggerKind}
                        onChange={(e) =>
                          setScheduleForm((f) => ({
                            ...f,
                            triggerKind: e.target.value as typeof scheduleForm.triggerKind,
                          }))
                        }
                        className="rounded border border-input bg-background px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-ring"
                      >
                        <option value="time_based">Time-based</option>
                        <option value="engine_hours">Engine hours</option>
                        <option value="calendar_date">Seasonal</option>
                      </select>
                    </div>
                    {scheduleForm.triggerKind === 'time_based' && (
                      <div className="flex flex-col gap-0.5">
                        <label className="text-[10px] text-muted-foreground">Every N days</label>
                        <input
                          type="number"
                          min={1}
                          value={scheduleForm.everyNDays}
                          onChange={(e) =>
                            setScheduleForm((f) => ({
                              ...f,
                              everyNDays: parseInt(e.target.value) || 1,
                            }))
                          }
                          className="rounded border border-input bg-background px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-ring"
                        />
                      </div>
                    )}
                    {scheduleForm.triggerKind === 'engine_hours' && (
                      <div className="flex flex-col gap-0.5">
                        <label className="text-[10px] text-muted-foreground">Every N hours</label>
                        <input
                          type="number"
                          min={1}
                          value={scheduleForm.everyNHours}
                          onChange={(e) =>
                            setScheduleForm((f) => ({
                              ...f,
                              everyNHours: parseInt(e.target.value) || 1,
                            }))
                          }
                          className="rounded border border-input bg-background px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-ring"
                        />
                      </div>
                    )}
                    {scheduleForm.triggerKind === 'calendar_date' && (
                      <div className="flex flex-col gap-0.5">
                        <label className="text-[10px] text-muted-foreground">Anchor</label>
                        <select
                          value={scheduleForm.anchor}
                          onChange={(e) =>
                            setScheduleForm((f) => ({
                              ...f,
                              anchor: e.target.value as typeof scheduleForm.anchor,
                            }))
                          }
                          className="rounded border border-input bg-background px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-ring"
                        >
                          <option value="season_start">Season start</option>
                          <option value="season_end">Season end</option>
                        </select>
                      </div>
                    )}
                    <div className="flex flex-col gap-0.5 col-span-2">
                      <label className="text-[10px] text-muted-foreground">
                        Instructions (optional)
                      </label>
                      <input
                        type="text"
                        value={scheduleForm.instructions}
                        onChange={(e) =>
                          setScheduleForm((f) => ({ ...f, instructions: e.target.value }))
                        }
                        placeholder="e.g. Use SAE 30 oil"
                        className="rounded border border-input bg-background px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-ring"
                      />
                    </div>
                  </div>
                  {addScheduleMutation.isError && (
                    <p className="text-xs text-destructive">
                      {addScheduleMutation.error instanceof ApiError
                        ? addScheduleMutation.error.message
                        : 'Add failed.'}
                    </p>
                  )}
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      className="h-7 text-xs px-2"
                      onClick={handleAddSchedule}
                      disabled={!scheduleForm.name.trim() || addScheduleMutation.isPending}
                    >
                      {addScheduleMutation.isPending ? 'Adding…' : 'Add'}
                    </Button>
                    <button
                      className="text-xs text-muted-foreground hover:text-foreground"
                      onClick={() => setAddingSchedule(false)}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function todayStr(): string {
  return new Date().toISOString().slice(0, 10);
}

function relativeDate(iso: string): string {
  const diffDays = Math.ceil((new Date(iso).getTime() - Date.now()) / 86400000);
  if (diffDays < 0) return `${Math.abs(diffDays)}d overdue`;
  if (diffDays === 0) return 'today';
  if (diffDays === 1) return 'tomorrow';
  return `in ${diffDays}d`;
}

function shortDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function CheckInModal({
  equipmentId,
  activeFlags,
  hasEngineHoursSchedule,
  onClose,
}: {
  equipmentId: string;
  activeFlags: ConditionFlag[];
  hasEngineHoursSchedule: boolean;
  onClose: () => void;
}) {
  const { propertyId } = Route.useParams();
  const qc = useQueryClient();
  const [idempotencyKey] = useState(() => crypto.randomUUID());
  const [engineHours, setEngineHours] = useState('');
  const [performedOn, setPerformedOn] = useState(todayStr());
  const [notes, setNotes] = useState('');
  const [flagValues, setFlagValues] = useState<Record<string, boolean>>({});

  const allFlagsAnswered = activeFlags.every((f) => f.id in flagValues);

  const mutation = useMutation({
    mutationFn: () =>
      api.post(
        `/v1/equipment/${equipmentId}/check-ins`,
        {
          engineHours: hasEngineHoursSchedule ? parseInt(engineHours, 10) : 0,
          performedOn: new Date(performedOn + 'T12:00:00').toISOString(),
          notes: notes.trim() || null,
          flagValues: activeFlags.map((f) => ({ flagId: f.id, value: flagValues[f.id] })),
        },
        { headers: { 'Idempotency-Key': idempotencyKey } },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['check-ins', equipmentId] });
      void qc.invalidateQueries({ queryKey: ['equipment', propertyId, false] });
      onClose();
    },
  });

  const canSubmit =
    (!hasEngineHoursSchedule || engineHours !== '') && allFlagsAnswered && !mutation.isPending;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div
        className="bg-background rounded-lg border border-border shadow-lg w-full max-w-md mx-4 p-6 flex flex-col gap-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">Log check-in</h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground text-xl leading-none">×</button>
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium">Date</label>
          <input
            type="date"
            value={performedOn}
            onChange={(e) => setPerformedOn(e.target.value)}
            className="rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>

        {hasEngineHoursSchedule && (
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium">Engine hours</label>
            <input
              type="number"
              min={0}
              value={engineHours}
              onChange={(e) => setEngineHours(e.target.value)}
              placeholder="0"
              className="rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>
        )}

        {activeFlags.length > 0 && (
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium">Condition flags</label>
            <div className="flex flex-col gap-2">
              {activeFlags.map((flag) => {
                const value = flagValues[flag.id];
                return (
                  <div key={flag.id} className="flex items-center gap-3">
                    <span className="text-sm flex-1">{flag.label}</span>
                    <div className="flex gap-1.5">
                      <button
                        type="button"
                        onClick={() => setFlagValues((fv) => ({ ...fv, [flag.id]: true }))}
                        className={cn(
                          'px-3 py-1 rounded text-xs border transition-colors',
                          value === true
                            ? 'border-green-400 bg-green-100 text-green-700 dark:bg-green-900 dark:border-green-600 dark:text-green-300'
                            : 'border-border bg-background text-muted-foreground hover:border-green-300',
                        )}
                      >
                        OK
                      </button>
                      <button
                        type="button"
                        onClick={() => setFlagValues((fv) => ({ ...fv, [flag.id]: false }))}
                        className={cn(
                          'px-3 py-1 rounded text-xs border transition-colors',
                          value === false
                            ? 'border-red-400 bg-red-100 text-red-700 dark:bg-red-900 dark:border-red-600 dark:text-red-300'
                            : 'border-border bg-background text-muted-foreground hover:border-red-300',
                        )}
                      >
                        Not OK
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
            {!allFlagsAnswered && (
              <p className="text-xs text-muted-foreground">Answer all flags to continue.</p>
            )}
          </div>
        )}

        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium">
            Notes <span className="font-normal text-muted-foreground">(optional)</span>
          </label>
          <input
            type="text"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Any observations…"
            className="rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>

        {mutation.isError && (
          <p className="text-sm text-destructive">
            {mutation.error instanceof ApiError
              ? mutation.error.message
              : 'Failed to save check-in.'}
          </p>
        )}

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => mutation.mutate()} disabled={!canSubmit}>
            {mutation.isPending ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </div>
    </div>
  );
}

function CheckInPanel({
  equipmentId,
  activeFlags,
  hasEngineHoursSchedule,
  isInsideSeason,
  isCheckInOverdue,
  nextCheckInDueAt,
}: {
  equipmentId: string;
  activeFlags: ConditionFlag[];
  hasEngineHoursSchedule: boolean;
  isInsideSeason: boolean;
  isCheckInOverdue: boolean;
  nextCheckInDueAt: string | null;
}) {
  const [showModal, setShowModal] = useState(false);

  const { data: checkInsData } = useQuery({
    queryKey: ['check-ins', equipmentId],
    queryFn: () =>
      api
        .get<{ checkIns: CheckIn[] }>(`/v1/equipment/${equipmentId}/check-ins?limit=5`)
        .then((r) => r.checkIns),
  });

  const statusPill = !isInsideSeason ? (
    <span className="text-[10px] text-muted-foreground">off-season</span>
  ) : isCheckInOverdue ? (
    <span className="text-[10px] text-red-500 font-medium">overdue</span>
  ) : nextCheckInDueAt ? (
    <span className="text-[10px] text-muted-foreground">{relativeDate(nextCheckInDueAt)}</span>
  ) : null;

  return (
    <>
      {showModal && (
        <CheckInModal
          equipmentId={equipmentId}
          activeFlags={activeFlags}
          hasEngineHoursSchedule={hasEngineHoursSchedule}
          onClose={() => setShowModal(false)}
        />
      )}
      <div className="mt-2 pt-2 border-t border-border">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-xs font-medium">Check-ins</span>
          {statusPill}
          <button
            className="text-xs text-muted-foreground hover:text-foreground ml-auto"
            onClick={() => setShowModal(true)}
          >
            + Log check-in
          </button>
        </div>

        {(checkInsData ?? []).length === 0 ? (
          <p className="text-xs text-muted-foreground">No check-ins recorded.</p>
        ) : (
          <div className="flex flex-col gap-1">
            {checkInsData?.map((ci) => {
              const issues = ci.flagValues.filter((fv) => !fv.isOk);
              return (
                <div key={ci.id} className="text-xs text-muted-foreground">
                  {shortDate(ci.performedOn)} · {ci.engineHours}h
                  {issues.length > 0 ? (
                    <span className="text-red-500">
                      {' '}· {issues.length} issue{issues.length > 1 ? 's' : ''} (
                      {issues.map((fv) => fv.flagLabelSnapshot).join(', ')})
                    </span>
                  ) : ci.flagValues.length > 0 ? (
                    <span className="text-green-600"> · all OK</span>
                  ) : null}
                  {ci.notes && <span className="italic"> — {ci.notes}</span>}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}

function EquipmentRow({
  eq,
  isOwner,
  propertyId,
  onArchiveToggle,
  archivePending,
  onOocToggle,
  oocPending,
}: {
  eq: Equipment;
  isOwner: boolean;
  propertyId: string;
  onArchiveToggle: () => void;
  archivePending: boolean;
  onOocToggle: () => void;
  oocPending: boolean;
}) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<EditState>({
    name: eq.name,
    type: eq.type,
    modelNumber: eq.modelNumber ?? '',
    usageSeasonStart: eq.usageSeasonStart,
    usageSeasonEnd: eq.usageSeasonEnd,
    checkInsRequired: eq.checkInsRequired !== false,
  });
  const [showFlags, setShowFlags] = useState(false);
  const [showSchedules, setShowSchedules] = useState(false);
  const [showCheckIns, setShowCheckIns] = useState(false);
  const [thumbnailUploading, setThumbnailUploading] = useState(false);
  const [thumbnailError, setThumbnailError] = useState<string | null>(null);
  const thumbnailInputRef = useRef<HTMLInputElement>(null);
  const [menuOpen, setMenuOpen] = useState(false);

  const { data: flagsData } = useQuery({
    queryKey: ['flags', eq.id],
    queryFn: () =>
      api
        .get<{ flags: ConditionFlag[] }>(`/v1/equipment/${eq.id}/condition-flags`)
        .then((r) => r.flags),
  });

  const { data: schedulesData } = useQuery({
    queryKey: ['schedules', eq.id],
    queryFn: () =>
      api
        .get<{ schedules: MaintenanceSchedule[] }>(`/v1/equipment/${eq.id}/schedules`)
        .then((r) => r.schedules),
  });

  const activeFlags = flagsData?.filter((f) => f.archivedAt === null) ?? [];
  const activeSchedules = schedulesData?.filter((s) => s.archivedAt === null) ?? [];
  const activeFlagCount = activeFlags.length;
  const activeScheduleCount = activeSchedules.length;
  const hasEngineHoursSchedule = activeSchedules.some((s) => s.triggerKind === 'engine_hours');
  const hasCheckInItems = activeFlagCount > 0 || activeScheduleCount > 0;

  const editMutation = useMutation({
    mutationFn: () =>
      api.patch('/v1/equipment/' + eq.id, {
        name: form.name.trim(),
        type: form.type,
        modelNumber: form.modelNumber.trim() || null,
        usageSeasonStart: form.usageSeasonStart,
        usageSeasonEnd: form.usageSeasonEnd,
        checkInsRequired: form.checkInsRequired,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['equipment', eq.id.slice(0, 8)] });
      setEditing(false);
    },
  });

  const removeThumbnailMutation = useMutation({
    mutationFn: () => api.delete(`/v1/equipment/${eq.id}/thumbnail`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['equipment', propertyId] }),
  });

  async function handleThumbnailSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0] ?? null;
    e.target.value = ''; // allow re-selecting the same file on a later change
    if (!file) return;

    if (file.size > 8 * 1024 * 1024) {
      setThumbnailError('Image is larger than the 8 MB limit.');
      return;
    }

    setThumbnailError(null);
    setThumbnailUploading(true);
    try {
      // Multipart straight to the API (same pattern as the property-map
      // upload) — the S3 bucket CORS doesn't permit browser POSTs.
      const fd = new FormData();
      fd.append('image', file);
      const token = getAccessToken();
      const headers: Record<string, string> = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const res = await fetch(
        `${(import.meta.env.VITE_API_URL as string | undefined) ?? ''}/v1/equipment/${eq.id}/thumbnail`,
        { method: 'POST', body: fd, headers, credentials: 'include' },
      );
      if (!res.ok) {
        let message = `Photo upload failed (${res.status}).`;
        try {
          const body = (await res.json()) as { error?: { message?: string } };
          if (body.error?.message) message = body.error.message;
        } catch {
          // non-JSON error body; keep the status-code message
        }
        throw new Error(message);
      }
      void qc.invalidateQueries({ queryKey: ['equipment', propertyId] });
    } catch (err) {
      setThumbnailError(
        err instanceof Error && err.message ? err.message : 'Photo upload failed.',
      );
    } finally {
      setThumbnailUploading(false);
    }
  }

  if (editing) {
    return (
      <div className="rounded-lg border border-border p-4 flex flex-col gap-3">
        <div className="grid grid-cols-2 gap-3">
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-muted-foreground">Name</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              className="rounded-md border border-input bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-muted-foreground">Type</label>
            <select
              value={form.type}
              onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))}
              className="rounded-md border border-input bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            >
              {EQUIPMENT_TYPES.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-muted-foreground">Season start (MM-DD)</label>
            <input
              type="text"
              value={form.usageSeasonStart}
              onChange={(e) => setForm((f) => ({ ...f, usageSeasonStart: e.target.value }))}
              placeholder="04-01"
              className="rounded-md border border-input bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-muted-foreground">Season end (MM-DD)</label>
            <input
              type="text"
              value={form.usageSeasonEnd}
              onChange={(e) => setForm((f) => ({ ...f, usageSeasonEnd: e.target.value }))}
              placeholder="10-31"
              className="rounded-md border border-input bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>
          <div className="flex flex-col gap-1 col-span-2">
            <label className="text-xs font-medium text-muted-foreground">Model number</label>
            <input
              type="text"
              value={form.modelNumber}
              onChange={(e) => setForm((f) => ({ ...f, modelNumber: e.target.value }))}
              className="rounded-md border border-input bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>
          <label className="col-span-2 flex items-center gap-1.5 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={form.checkInsRequired}
              onChange={(e) => setForm((f) => ({ ...f, checkInsRequired: e.target.checked }))}
            />
            Requires regular check-ins
          </label>
        </div>
        {editMutation.isError && (
          <p className="text-sm text-destructive">
            {editMutation.error instanceof ApiError ? editMutation.error.message : 'Save failed.'}
          </p>
        )}
        <div className="flex gap-2">
          <Button
            size="sm"
            onClick={() => editMutation.mutate()}
            disabled={!form.name.trim() || editMutation.isPending}
          >
            {editMutation.isPending ? 'Saving…' : 'Save'}
          </Button>
          <Button variant="outline" size="sm" onClick={() => setEditing(false)}>
            Cancel
          </Button>
        </div>
      </div>
    );
  }

  const typeLabel = EQUIPMENT_TYPES.find((t) => t.value === eq.type)?.label ?? eq.type;

  return (
    <div className={cn('rounded-lg border border-border px-4 py-3', eq.archivedAt && 'opacity-60')}>
      <div className="flex items-center gap-3">
        {isOwner && !eq.archivedAt && (
          <input
            ref={thumbnailInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleThumbnailSelect}
          />
        )}
        {/* The photo itself is the upload affordance — click to add/replace. */}
        {eq.thumbnailUrl ? (
          <button
            type="button"
            className={cn(
              'shrink-0 rounded-md',
              isOwner && !eq.archivedAt ? 'cursor-pointer' : 'cursor-default',
              thumbnailUploading && 'animate-pulse',
            )}
            onClick={() => {
              if (isOwner && !eq.archivedAt) thumbnailInputRef.current?.click();
            }}
            title={isOwner && !eq.archivedAt ? 'Change photo' : undefined}
            disabled={thumbnailUploading}
          >
            <img
              src={eq.thumbnailUrl}
              alt=""
              className="h-12 w-12 rounded-md object-contain bg-muted"
            />
          </button>
        ) : isOwner && !eq.archivedAt ? (
          <button
            type="button"
            className={cn(
              'h-12 w-12 shrink-0 rounded-md border border-dashed border-border text-lg leading-none text-muted-foreground hover:border-ring hover:text-foreground',
              thumbnailUploading && 'animate-pulse',
            )}
            onClick={() => thumbnailInputRef.current?.click()}
            title="Add photo"
            disabled={thumbnailUploading}
          >
            +
          </button>
        ) : null}
        <div className="flex-1 min-w-0">
          <div className="font-medium text-sm">{eq.name}</div>
          <div className="text-xs text-muted-foreground">
            {typeLabel} · {eq.usageSeasonStart} – {eq.usageSeasonEnd}
            {eq.modelNumber && ` · ${eq.modelNumber}`}
          </div>
        </div>
        {!eq.archivedAt && eq.outOfCommissionAt && (
          <span className="text-xs text-muted-foreground shrink-0">⏸ Out of commission</span>
        )}
        {!eq.archivedAt && eq.isCheckInOverdue && (
          <span className="text-xs text-amber-600 shrink-0">Check-in overdue</span>
        )}
        {eq.archivedAt && (
          <span className="text-xs text-muted-foreground shrink-0">Archived</span>
        )}
        {isOwner && (
          <div className="relative flex items-center gap-2 shrink-0">
            {!eq.archivedAt && (
              <button
                className="text-xs text-muted-foreground hover:text-foreground"
                onClick={() => setEditing(true)}
              >
                Edit
              </button>
            )}
            <button
              className="rounded px-1.5 py-0.5 text-sm leading-none text-muted-foreground hover:bg-muted hover:text-foreground"
              onClick={() => setMenuOpen((v) => !v)}
              aria-label="More actions"
            >
              ⋯
            </button>
            {menuOpen && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setMenuOpen(false)} />
                <div className="absolute right-0 top-7 z-20 w-48 rounded-md border border-border bg-card py-1 shadow-md">
                  {!eq.archivedAt && (
                    <button
                      className="block w-full px-3 py-1.5 text-left text-xs hover:bg-muted disabled:opacity-50"
                      onClick={() => { setMenuOpen(false); onOocToggle(); }}
                      disabled={oocPending}
                    >
                      {eq.outOfCommissionAt ? 'Return to service' : 'Mark out of commission'}
                    </button>
                  )}
                  {!eq.archivedAt && eq.thumbnailUrl && (
                    <button
                      className="block w-full px-3 py-1.5 text-left text-xs hover:bg-muted disabled:opacity-50"
                      onClick={() => { setMenuOpen(false); removeThumbnailMutation.mutate(); }}
                      disabled={removeThumbnailMutation.isPending}
                    >
                      Remove photo
                    </button>
                  )}
                  <button
                    className="block w-full px-3 py-1.5 text-left text-xs text-destructive hover:bg-muted disabled:opacity-50"
                    onClick={() => { setMenuOpen(false); onArchiveToggle(); }}
                    disabled={archivePending}
                  >
                    {eq.archivedAt ? 'Unarchive' : 'Archive'}
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {thumbnailError && (
        <p className="text-xs text-destructive mt-1">{thumbnailError}</p>
      )}
      {removeThumbnailMutation.isError && (
        <p className="text-xs text-destructive mt-1">
          {removeThumbnailMutation.error instanceof ApiError
            ? removeThumbnailMutation.error.message
            : 'Failed to remove photo.'}
        </p>
      )}

      {!eq.archivedAt && (
        <div className="mt-2 flex gap-3">
          <button
            className="text-xs text-muted-foreground hover:text-foreground"
            onClick={() => setShowFlags((v) => !v)}
          >
            {showFlags ? '▾' : '▸'} Flags ({activeFlagCount})
          </button>
          <button
            className="text-xs text-muted-foreground hover:text-foreground"
            onClick={() => setShowSchedules((v) => !v)}
          >
            {showSchedules ? '▾' : '▸'} Schedules ({activeScheduleCount})
          </button>
          {hasCheckInItems && (
            <button
              className={cn(
                'text-xs hover:text-foreground',
                eq.isCheckInOverdue
                  ? 'text-amber-500 font-medium'
                  : 'text-muted-foreground',
              )}
              onClick={() => setShowCheckIns((v) => !v)}
            >
              {showCheckIns ? '▾' : '▸'} Check-ins
            </button>
          )}
        </div>
      )}

      {!eq.archivedAt && showFlags && (
        <FlagsPanel equipmentId={eq.id} isOwner={isOwner} />
      )}

      {!eq.archivedAt && showSchedules && (
        <SchedulesPanel equipmentId={eq.id} isOwner={isOwner} />
      )}

      {!eq.archivedAt && showCheckIns && (
        <CheckInPanel
          equipmentId={eq.id}
          activeFlags={activeFlags}
          hasEngineHoursSchedule={hasEngineHoursSchedule}
          isInsideSeason={eq.isInsideSeason}
          isCheckInOverdue={eq.isCheckInOverdue}
          nextCheckInDueAt={eq.nextCheckInDueAt}
        />
      )}
    </div>
  );
}

function PropertyEquipment() {
  const { propertyId } = Route.useParams();
  const qc = useQueryClient();

  const { data: propData } = useQuery({
    queryKey: ['property', propertyId],
    queryFn: () =>
      api.get<{ property: PropertyDetail }>('/v1/properties/' + propertyId),
  });

  const isOwner = propData?.property?.role === 'owner';
  const [showArchived, setShowArchived] = useState(false);

  const { data: equipment, isPending } = useQuery({
    queryKey: ['equipment', propertyId, showArchived],
    queryFn: () => {
      const params = showArchived ? '?includeArchived=true' : '';
      return api
        .get<{ equipment: Equipment[] }>('/v1/properties/' + propertyId + '/equipment' + params)
        .then((r) => r.equipment);
    },
  });

  const archiveMutation = useMutation({
    mutationFn: ({ id, archive }: { id: string; archive: boolean }) =>
      api.post('/v1/equipment/' + id + (archive ? '/archive' : '/unarchive'), {}),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['equipment', propertyId] }),
  });

  const oocMutation = useMutation({
    mutationFn: ({ id, outOfCommission }: { id: string; outOfCommission: boolean }) =>
      api.post(
        '/v1/equipment/' + id + (outOfCommission ? '/out-of-commission' : '/return-to-service'),
        {},
      ),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['equipment', propertyId] }),
  });

  // Create form
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({
    name: '',
    type: 'riding_mower',
    usageSeasonStart: '04-01',
    usageSeasonEnd: '10-31',
    checkInsRequired: true,
  });
  const [createError, setCreateError] = useState<string | null>(null);

  const createMutation = useMutation({
    mutationFn: () =>
      api.post('/v1/properties/' + propertyId + '/equipment', {
        name: createForm.name.trim(),
        type: createForm.type,
        usageSeasonStart: createForm.usageSeasonStart,
        usageSeasonEnd: createForm.usageSeasonEnd,
        checkInsRequired: createForm.checkInsRequired,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['equipment', propertyId] });
      setShowCreate(false);
      setCreateForm({ name: '', type: 'riding_mower', usageSeasonStart: '04-01', usageSeasonEnd: '10-31', checkInsRequired: true });
      setCreateError(null);
    },
    onError: (err) => {
      setCreateError(
        err instanceof ApiError ? err.message : 'Create failed.',
      );
    },
  });

  const active = equipment?.filter((e) => !e.archivedAt) ?? [];
  const archived = equipment?.filter((e) => e.archivedAt) ?? [];

  return (
    <div className="p-6 max-w-2xl">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold">Equipment</h2>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-1.5 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
            />
            Show archived
          </label>
          {isOwner && !showCreate && (
            <Button size="sm" onClick={() => setShowCreate(true)}>
              Add equipment
            </Button>
          )}
        </div>
      </div>

      {isOwner && showCreate && (
        <div className="rounded-lg border border-border p-4 mb-4 flex flex-col gap-3">
          <h3 className="text-sm font-medium">New equipment</h3>
          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-muted-foreground">Name</label>
              <input
                type="text"
                value={createForm.name}
                onChange={(e) => setCreateForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="e.g. Big John"
                className="rounded-md border border-input bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-muted-foreground">Type</label>
              <select
                value={createForm.type}
                onChange={(e) => setCreateForm((f) => ({ ...f, type: e.target.value }))}
                className="rounded-md border border-input bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              >
                {EQUIPMENT_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-muted-foreground">Season start (MM-DD)</label>
              <input
                type="text"
                value={createForm.usageSeasonStart}
                onChange={(e) => setCreateForm((f) => ({ ...f, usageSeasonStart: e.target.value }))}
                placeholder="04-01"
                className="rounded-md border border-input bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-muted-foreground">Season end (MM-DD)</label>
              <input
                type="text"
                value={createForm.usageSeasonEnd}
                onChange={(e) => setCreateForm((f) => ({ ...f, usageSeasonEnd: e.target.value }))}
                placeholder="10-31"
                className="rounded-md border border-input bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
            <label className="col-span-2 flex items-center gap-1.5 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={createForm.checkInsRequired}
                onChange={(e) => setCreateForm((f) => ({ ...f, checkInsRequired: e.target.checked }))}
              />
              Requires regular check-ins
            </label>
          </div>
          {createError && <p className="text-sm text-destructive">{createError}</p>}
          <div className="flex gap-2">
            <Button
              size="sm"
              onClick={() => createMutation.mutate()}
              disabled={!createForm.name.trim() || createMutation.isPending}
            >
              {createMutation.isPending ? 'Creating…' : 'Create'}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => { setShowCreate(false); setCreateError(null); }}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      {isPending ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : (
        <div className="flex flex-col gap-2">
          {active.length === 0 && !showArchived && (
            <p className="text-sm text-muted-foreground">No equipment yet.</p>
          )}
          {active.map((eq) => (
            <EquipmentRow
              key={eq.id}
              eq={eq}
              isOwner={isOwner}
              propertyId={propertyId}
              onArchiveToggle={() => archiveMutation.mutate({ id: eq.id, archive: !eq.archivedAt })}
              archivePending={archiveMutation.isPending}
              onOocToggle={() => oocMutation.mutate({ id: eq.id, outOfCommission: !eq.outOfCommissionAt })}
              oocPending={oocMutation.isPending}
            />
          ))}
          {showArchived && archived.length > 0 && (
            <>
              <div className="text-xs text-muted-foreground mt-2 mb-1">Archived</div>
              {archived.map((eq) => (
                <EquipmentRow
                  key={eq.id}
                  eq={eq}
                  isOwner={isOwner}
                  propertyId={propertyId}
                  onArchiveToggle={() => archiveMutation.mutate({ id: eq.id, archive: !eq.archivedAt })}
                  archivePending={archiveMutation.isPending}
                  onOocToggle={() => oocMutation.mutate({ id: eq.id, outOfCommission: !eq.outOfCommissionAt })}
                  oocPending={oocMutation.isPending}
                />
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}
